import { makeAutoObservable, runInAction } from "mobx"
import isEmpty from 'lodash/isEmpty'
import {
  ICompetition,
  Status,
  IFetchCompetitionStatus,
  ICompetitionsPayload,
  ICompetitionErrorMessages,
  ICreateCompetitionPayload,
  CompetitionType,
  IPayoutOptions,
  ICompetitionStaff,
  ICompetitionCategory,
  IPagination,
  IMyCompetitions,
  TSubscriptionOptionsResponse
} from "../utils/interfaces";
import {status, competitionRequest, NEW, FAV, ALL, INVOLVED} from "../utils/constants";
import { processError, retrieveErrorMessage } from "../utils/helpers";
import { 
  fetchMyCompetitionsRequest,
  fetchCompetitionCategoryRequest,
  fetchAllCompetitionsRequest,
  getPopularCompetitionsRequest,
  fetchFavouriteCompetitionRequest,
  fetchMyPrivateCompetitionsRequest,
  fetchSubscriptionOptionsRequest,
} from "../utils/apiRequests";
import uiStore from "./uiStore";


interface IFetchMyCompetition {
  getNext?: boolean,
  page?: number,
}

const defaultCompetitionFields = {
  competitionDetails: ({} as ICompetition),
  payoutOptions: ({} as IPayoutOptions),
  createDetailsStatus: null,
  currentStep: 0,
  stepTitle: '',
}

class CompetitionStore {
  favouriteCompetitions: ICompetition[] = []
  favouriteCompetitionsStatus: Status = null

  popularCompetitions: ICompetition[] = []
  popularCompetitionsStatus: Status = null
  
  similarCompetitions: ICompetition[] = []
  similarCompetitionsStatus: Status = null

  categories: ICompetitionCategory[] = []
  categoriesStatus: Status = null

  createCompetitionStatus: Status = null
  createdCompetition: ICompetition | null = null
  createCompetitionErrMessage: string = ''

  fetchCompetitionStatus: IFetchCompetitionStatus = ({} as IFetchCompetitionStatus)
  dashboardCompetitions: ICompetitionsPayload = ({} as ICompetitionsPayload)
  fetchCompetitionErrMessages: ICompetitionErrorMessages = ({} as ICompetitionErrorMessages)

  myCompetitions:  IMyCompetitions = {}
  myCompetitionsStatus: Status = null

  myPrivateCompetitions: IMyCompetitions = {}
  myPrivateCompetitionsStatus: Status = null

  competitions: { 
    results: ICompetition[],
    pagination: IPagination
  } = { results: [], pagination: {} as IPagination }
  competitionsStatus: Status = null

  competitionStaff: ICompetitionStaff[] = []
  competitionStaffStatus: Status = null

  subscriptionOptions: TSubscriptionOptionsResponse | null = null
  subscriptionOptionsStatus: Status = null

  competitionFields: ICreateCompetitionPayload = {...defaultCompetitionFields}

  constructor() {
    makeAutoObservable(this)
  }

  async fetchDashboardCompetitions(type: CompetitionType = 'new') {
    if (this.fetchNewCompetitionLoading) return

    runInAction(() => {
      this.fetchCompetitionStatus[type] = status.LOADING
    })

    try {
      const response = await competitionRequest[type]()

      runInAction(() => {
        this.dashboardCompetitions[type] = response.data
        this.fetchCompetitionStatus[type] = status.COMPLETE
      })
    } catch (e) { 
      runInAction(() => {
        this.fetchCompetitionStatus[type] = status.FAILED
        this.fetchCompetitionErrMessages[type] = retrieveErrorMessage(e)
      })
    }
  }

  async fetchCompetitions({
    category,
    page: requiredPage,
    getNext 
  }: {
    category?: string,
    page?: number,
    getNext?: boolean
  }) {
    const { results, pagination } = this.competitions
    if (this.fetchingCompetitions) return

    runInAction(() => {
      this.competitionsStatus = status.LOADING
      if (!getNext && !this.competitions.results.length) {
        uiStore.setLoadingScreenMessage('Fetching campaigns')
      }
    })

    try {
      const page = getNext ? (pagination.current_page || 0) + 1 : requiredPage
      const response = await fetchAllCompetitionsRequest(category, page)
      const { results: newResults, pagination: newPagination } = response.data

      runInAction(() => {
        this.competitionsStatus = status.COMPLETE
        if (getNext) {
          this.competitions = { 
            results: [...results, ...newResults ],
            pagination: newPagination
          }
        } else {
          this.competitions = response.data
        }
      })
    } catch (e) {
      processError(e)
      runInAction(() => {
        this.competitionsStatus = status.FAILED
      })
    }

    uiStore.setLoadingScreenMessage('')
  }

  async fetchMyCompetitions({ getNext = false, page = 1}: IFetchMyCompetition) {
    const { results = [], pagination  } = this.myCompetitions
    if (this.fetchingMyCompetitions) return

    if (!getNext) {
      runInAction(() => this.myCompetitionsStatus = status.LOADING)
    }

    try {
      const pageToFetch = getNext ? (pagination?.current_page || 0) + 1 : 1
      const response = await fetchMyCompetitionsRequest(pageToFetch)
      const { results: newResults = [], pagination: newPagination } = response.data
      
      runInAction(() => {
        this.myCompetitionsStatus = status.COMPLETE
        if (getNext) {
          this.myCompetitions = { 
            results: [...results, ...newResults ],
            pagination: newPagination
          }
        } else {
          this.myCompetitions = response.data
        }
      })
    } catch {
      runInAction(() => this.myCompetitionsStatus = status.FAILED)
    }
  }

  async fetchMyPrivateCompetitions({ getNext = false, page = 1}: IFetchMyCompetition) {
    const { results = [], pagination  } = this.myPrivateCompetitions
    if (this.fetchingMyPrivateCompetitions) return

    if (!getNext) {
      runInAction(() => this.myPrivateCompetitionsStatus = status.LOADING)
    }

    try {
      const pageToFetch = getNext ? (pagination?.current_page || 0) + 1 : 1
      const response = await fetchMyPrivateCompetitionsRequest(pageToFetch)
      const { results: newResults = [], pagination: newPagination } = response.data
      
      runInAction(() => {
        this.myPrivateCompetitionsStatus = status.COMPLETE
        this.myPrivateCompetitions = { 
          results: getNext ? [...results, ...newResults] : response.data.results,
          pagination: newPagination
        }
      })
    } catch {
      runInAction(() => this.myPrivateCompetitionsStatus = status.FAILED)
    }
  }

  async fetchCompetitionCategories() {
    if (this.fetchingCompetitionCategories) return 

    try {
      const response = await fetchCompetitionCategoryRequest()

      runInAction(() => {
        this.categoriesStatus = status.COMPLETE
        this.categories = response.data
      })
    } catch (e) {
      processError(e)
      runInAction(() => {
        this.categoriesStatus = status.FAILED
      })
    }
  }

  async fetchSubscriptionOptions() {
    if (this.subscriptionOptionsStatus === status.LOADING) return

    runInAction(() => {
      this.subscriptionOptionsStatus = status.LOADING
    })

    try {
      const response = await fetchSubscriptionOptionsRequest()

      runInAction(() => {
        this.subscriptionOptionsStatus = status.COMPLETE
        this.subscriptionOptions = response.data
      })
    } catch (e) {
      processError(e)
      runInAction(() => {
        this.subscriptionOptionsStatus = status.FAILED
      })
    }
  }

  async fetchPopularCompetitions() {
    if (!this.popularCompetitionsPristine || this.fetchingPopularCompetitions) return
    this.setPopularCompetitionsStatus(status.LOADING)

    try {
      const response = await getPopularCompetitionsRequest()
      this.storePopularCompetitions(response.data.data)
      this.setPopularCompetitionsStatus(status.COMPLETE)
    } catch (e) {
      retrieveErrorMessage(e)
      this.setPopularCompetitionsStatus(status.FAILED)
    }
  }

  async fetchFavouriteCompetitions() {
    if (this.fetchingFavouriteCompetitions) return
    runInAction(() => this.favouriteCompetitionsStatus = status.LOADING)

    try {
      const response = await fetchFavouriteCompetitionRequest()
      runInAction(() => {
        this.favouriteCompetitionsStatus = status.COMPLETE
        this.favouriteCompetitions = response.data.data
      })
    } catch (e) {
      runInAction(() => {
        this.favouriteCompetitionsStatus = status.FAILED
      })
    }
  }

  async fetchSimilarCompetitions(category: string) {
    if (this.fetchingSimilarCompetitions) return
    this.setSimilarCompetitionsStatus(status.LOADING)

    try {
      const response = await fetchAllCompetitionsRequest(category, 1)
      this.storeSimilarCompetitions(response.data.results)
      this.setSimilarCompetitionsStatus(status.COMPLETE)
    } catch (e) {
      retrieveErrorMessage(e)
      this.setSimilarCompetitionsStatus(status.FAILED)
    }
  }

  updateFavouritesData(competition: ICompetition) {
    if (this.favouriteCompetitions.find(c => c.id === competition.id)) {
      this.favouriteCompetitions = this.favouriteCompetitions.filter(c => c.id !== competition.id)
    } else {
      this.favouriteCompetitions = this.favouriteCompetitions.concat(competition)
    }
  }

  storePopularCompetitions(competitions: ICompetition[]) {
    this.popularCompetitions = competitions
  }

  setPopularCompetitionsStatus(status: Status) {
    this.popularCompetitionsStatus = status
  }

  storeSimilarCompetitions(competitions: ICompetition[]) {
    this.similarCompetitions = competitions
  }

  setSimilarCompetitionsStatus(status: Status) {
    this.similarCompetitionsStatus = status
  }

  clearCompetitionData() {
    this.competitions = { results: [], pagination: {} as IPagination }
  }

  getPlanByCode(code: number) {
    return this.subscriptionOptions 
      ? Object.values(this.subscriptionOptions).find(plan => plan.subscription_code === code) 
      : undefined
  }

  get fetchNewCompetitionLoading() {
    return this.fetchCompetitionStatus[NEW] === status.LOADING
  }

  get fetchNewCompetitionComplete() {
    return this.fetchCompetitionStatus[NEW] === status.COMPLETE
  }

  get fetchNewCompetitionFailed() {
    return this.fetchCompetitionStatus[NEW] === status.FAILED
  }

  get fetchInvolvedCompetitionLoading() {
    return this.fetchCompetitionStatus[INVOLVED] === status.LOADING
  }

  get fetchInvolvedCompetitionComplete() {
    return this.fetchCompetitionStatus[INVOLVED] === status.COMPLETE
  }

  get voterDashboardHomeLoaded() {
    return this.fetchInvolvedCompetitionComplete && this.fetchFavouriteCompetitionComplete && this.fetchNewCompetitionComplete
  }

  get shouldShowDefaultStateOnDashboard() {
    return this.voterDashboardHomeLoaded 
      && isEmpty(this.dashboardCompetitions.involved)
      && isEmpty(this.dashboardCompetitions.favourite) 
      // && isEmpty(this.dashboardCompetitions.new)
  }

  get fetchInvolvedCompetitionFailed() {
    return this.fetchCompetitionStatus[INVOLVED] === status.FAILED
  }

  get fetchFavouriteCompetitionLoading() {
    return this.fetchCompetitionStatus[FAV] === status.LOADING
  }

  get fetchFavouriteCompetitionComplete() {
    return this.favouriteCompetitionsStatus === status.COMPLETE
  }

  get fetchFavouriteCompetitionFailed() {
    return this.fetchCompetitionStatus[FAV] === status.FAILED
  }

  get fetchAllCompetitionsLoading() {
    return this.fetchCompetitionStatus[ALL] === status.LOADING
  }

  get fetchAllCompetitionsComplete() {
    return this.fetchCompetitionStatus[ALL] === status.COMPLETE
  }

  get fetchAllCompetitionsFailed() {
    return this.fetchCompetitionStatus[ALL] === status.FAILED
  }

  get fetchingMyCompetitions() {
    return this.myCompetitionsStatus === status.LOADING
  }

  get fetchedMyCompetitions() {
    return this.myCompetitionsStatus === status.COMPLETE
  }

  get fetchingCompetitionStaff() {
    return this.competitionStaffStatus === status.LOADING
  }
  
  get fetchingCompetitionCategories() {
    return this.categoriesStatus === status.LOADING
  }

  get fetchingCompetitions() {
    return this.competitionsStatus === status.LOADING
  }

  get popularCompetitionsPristine() {
    return !this.popularCompetitions?.length && this.popularCompetitionsStatus === null
  }
  
  get fetchingPopularCompetitions() {
    return this.popularCompetitionsStatus === status.LOADING
  }

  get failedToFetchPopularCompetitions() {
    return this.popularCompetitionsStatus === status.FAILED
  }

  get fetchedPopularCompetitions() {
    return this.popularCompetitionsStatus === status.COMPLETE
  }

  get hasPopularCompetitions() {
    return !!this.popularCompetitions?.length
  }

  get similarCompetitionsPristine() {
    return !this.similarCompetitions?.length && this.similarCompetitionsStatus === null
  }
  
  get fetchingSimilarCompetitions() {
    return this.similarCompetitionsStatus === status.LOADING
  }

  get failedToFetchSimilarCompetitions() {
    return this.similarCompetitionsStatus === status.FAILED
  }

  get fetchedSimilarCompetitions() {
    return this.similarCompetitionsStatus === status.COMPLETE
  }

  get hasSimilarCompetitions() {
    return !!this.similarCompetitions?.length
  }

  get fetchingFavouriteCompetitions() {
    return this.favouriteCompetitionsStatus === status.LOADING
  }

  get userCompetitions() {
    return this.myCompetitions.results || []
  }

  get userCompetitionPaginationInfo() {
    return this.myCompetitions.pagination
  }

  get noUserCompetitions() {
    return !this.userCompetitions?.length
  }

  get fetchingMyPrivateCompetitions() {
    return this.myPrivateCompetitionsStatus === status.LOADING
  }

  get fetchedMyPrivateCompetitions() {
    return this.myPrivateCompetitionsStatus === status.COMPLETE
  }

  get failedToFetchMyPrivateCompetitions() {
    return this.myPrivateCompetitionsStatus === status.FAILED
  }

  get privateCompetitionsPaginationInfo() {
    return this.myPrivateCompetitions.pagination
  }

  get noPrivateCompetitions() {
    return !(this.myPrivateCompetitions.results || []).length
  }

  get privateCompetitions() {
    return this.myPrivateCompetitions.results || []
  }
}

const competitionStore = new CompetitionStore()
export default competitionStore
